import { PERMISSIONS } from '../constants/permissions';
import type { LeaveModuleCapabilities } from '../types/leave';
import { hasPermission } from '../utils/permissions';

/**
 * Leave capabilities from mounted backend routes (arkhrmsapi leave.routes.js).
 *
 * Mounted:
 * - GET /leave/dashboard, /leave/balances, /leave/history
 * - GET /leave/session-types  (sp_lev_list_session_types)
 * - GET /leave/:leaveApplicationId
 * - POST /leave
 * - POST /leave/:id/cancel|approve|reject
 *
 * Admin list/approval reuse workflow pending inbox with entityType=LEAVE_APPLICATION.
 * Leave types catalog is not mounted — apply uses balances for type/policy IDs.
 * HR Admin leave balance management is mounted at /leave-balances (SETTINGS_VIEW / SETTINGS_UPDATE).
 */
export const LEAVE_ROUTE_CAPABILITIES = {
  balance: true,
  history: true,
  details: true,
  apply: true,
  sessionTypes: true,
  cancel: true,
  types: false,
  adminRequests: true,
  leaveApproval: true,
  approveAction: true,
  rejectAction: true,
} as const;

export function getLeaveModuleCapabilities(
  permissions: readonly string[],
): LeaveModuleCapabilities {
  const canRead = hasPermission(permissions, PERMISSIONS.LEAVE_APPLY);
  const canApprove = hasPermission(permissions, PERMISSIONS.LEAVE_APPROVE);
  return {
    canViewBalance: canRead && LEAVE_ROUTE_CAPABILITIES.balance,
    canViewHistory: canRead && LEAVE_ROUTE_CAPABILITIES.history,
    canViewDetails: (canRead || canApprove) && LEAVE_ROUTE_CAPABILITIES.details,
    canApply: canRead && LEAVE_ROUTE_CAPABILITIES.apply,
    canViewSessionTypes: canRead && LEAVE_ROUTE_CAPABILITIES.sessionTypes,
    canCancel: (canRead || canApprove) && LEAVE_ROUTE_CAPABILITIES.cancel,
    canViewAdminRequests:
      canApprove && LEAVE_ROUTE_CAPABILITIES.adminRequests,
    canAccessLeaveApproval:
      canApprove && LEAVE_ROUTE_CAPABILITIES.leaveApproval,
  };
}
